/**
 * <back-in-stock-form> — the "Notify me" signup inside the back-in-stock drawer.
 *
 * Replaces the old {% form 'contact' %} submission (AAS-5067), which POSTed to
 * Shopify's /contact: hCaptcha-gated, full page reload, and the signup landed as
 * an email in the CS inbox rather than in the CRM. This posts straight to the
 * middleware instead — same pattern as newsletter-card.js and return-form.js.
 *
 *   POST {settings.middleware_api_base_url}/back-in-stock/subscribe
 *   { email, country, language, product_sku, variant_id }
 *
 * `includeLocale: false` is REQUIRED: AtomicApi injects `locale` into every body
 * by default and the middleware rejects unknown keys.
 *
 * The variant the shopper is signing up for is NOT read from Liquid at render
 * time — the drawer's summary is swapped per variant and its dropdowns let the
 * shopper re-pick inside the drawer. Both paths write the three hidden inputs
 * (variant title / sku / GID) in theme.js, and this element reads them at submit
 * time, so the payload always matches what is on screen.
 */
if (!customElements.get('back-in-stock-form')) {
  class BackInStockForm extends HTMLElement {
    constructor() {
      super();
      this.emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.input = this.form.querySelector('[data-bis-email]');
      this.field = this.form.querySelector('.back-in-stock-modal__field');
      this.consent = this.form.querySelector('[data-bis-consent]');
      this.consentLabel = this.consent ? this.consent.closest('.back-in-stock-modal__check') : null;
      this.message = this.form.querySelector('.back-in-stock-modal__message');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton ? this.submitButton.textContent.trim() : '';
      this.defaultEmail = this.input ? this.input.value : '';
      this.isSubmitting = false;
      this.isDone = false;
      // Bumped by every submit and by reset(), so a response that lands after
      // the drawer was closed and the form reset is discarded instead of
      // painting success onto a form the shopper has already left.
      this.submitId = 0;

      this._onSubmit = this.onSubmit.bind(this);
      this._onEdit = this.clearErrors.bind(this);
      this.form.addEventListener('submit', this._onSubmit);
      this.input?.addEventListener('input', this._onEdit);
      this.consent?.addEventListener('change', this._onEdit);

      // Success locks the form (see showSuccess) — clear it when the drawer
      // closes so the next open starts from the default state rather than
      // needing a page reload. drawer:close fires from info-drawer.js.
      this.drawer = this.closest('info-drawer');
      this._onDrawerClose = this.onDrawerClose.bind(this);
      this.drawer?.addEventListener('drawer:close', this._onDrawerClose);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._onSubmit);
      this.input?.removeEventListener('input', this._onEdit);
      this.consent?.removeEventListener('change', this._onEdit);
      this.drawer?.removeEventListener('drawer:close', this._onDrawerClose);
      clearTimeout(this._resetTimer);
    }

    /* Wait out the panel's 0.3s close transition so the reset isn't visible as
       a flash of the form clearing itself on the way out. */
    onDrawerClose() {
      clearTimeout(this._resetTimer);
      this._resetTimer = setTimeout(() => this.reset(), 350);
    }

    reset() {
      this.submitId += 1;
      this.isDone = false;
      this.isSubmitting = false;
      this.form.removeAttribute('aria-busy');
      this.setMessage('', null);
      this.field?.classList.remove('back-in-stock-modal__field--error');
      this.consentLabel?.classList.remove('back-in-stock-modal__check--error');
      if (this.input) {
        this.input.removeAttribute('readonly');
        this.input.removeAttribute('aria-invalid');
        this.input.value = this.defaultEmail;
      }
      if (this.consent) {
        this.consent.checked = false;
        this.consent.removeAttribute('aria-invalid');
      }
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.textContent = this.submitLabel;
      }
    }

    get strings() {
      return window.backInStockStrings || {};
    }

    setMessage(text, type) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.hidden = !text;
      this.message.classList.toggle('back-in-stock-modal__message--error', type === 'error');
      this.message.classList.toggle('back-in-stock-modal__message--success', type === 'success');
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.form.setAttribute('aria-busy', String(isLoading));
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.textContent = isLoading ? this.strings.loading || this.submitLabel : this.submitLabel;
    }

    clearErrors() {
      if (this.isDone) return;
      this.field?.classList.remove('back-in-stock-modal__field--error');
      this.consentLabel?.classList.remove('back-in-stock-modal__check--error');
      this.input?.removeAttribute('aria-invalid');
      this.consent?.removeAttribute('aria-invalid');
      if (this.message && !this.message.classList.contains('back-in-stock-modal__message--success')) {
        this.setMessage('', null);
      }
    }

    showError(text, invalidField) {
      this.setLoading(false);
      this.setMessage(text || this.strings.apiError || '', 'error');
      if (!invalidField) return;
      invalidField.setAttribute('aria-invalid', 'true');
      if (invalidField === this.consent) {
        this.consentLabel?.classList.add('back-in-stock-modal__check--error');
      } else {
        this.field?.classList.add('back-in-stock-modal__field--error');
      }
      if (typeof invalidField.focus === 'function') invalidField.focus();
    }

    /* Inline success — the drawer deliberately stays open (it carries the
       product summary the shopper just signed up for). The form locks so a
       second submit can't duplicate the signup. */
    showSuccess(text) {
      this.clearErrors();
      this.isDone = true;
      this.setLoading(false);
      this.setMessage(text, 'success');
      this.input?.setAttribute('readonly', 'readonly');
      if (this.submitButton) this.submitButton.disabled = true;
    }

    validate() {
      const email = (this.input?.value || '').trim();
      if (!email || !this.emailPattern.test(email)) {
        this.showError(this.strings.emailError, this.input);
        return false;
      }
      if (this.consent && !this.consent.checked) {
        this.showError(this.strings.consentError, this.consent);
        return false;
      }
      this.clearErrors();
      return true;
    }

    /* The five keys the middleware defines — nothing else, it rejects unknown
       keys. country/language come from the storefront market (window.theme.market,
       js-variables.liquid), which resolves localization.language rather than
       request.locale, so multi-language markets (CH de/fr/it, BE nl/fr) stay
       correct. sku/GID come from the hidden inputs kept in sync by theme.js. */
    getPayload() {
      const market = (window.theme && window.theme.market) || {};
      return {
        email: (this.input?.value || '').trim(),
        country: (market.country_code || '').toUpperCase(),
        language: (market.language_code || '').toUpperCase(),
        product_sku: this.form.querySelector('[data-bis-sku-input]')?.value || '',
        variant_id: this.form.querySelector('[data-bis-variant-id-input]')?.value || '',
      };
    }

    /* product_stock_alert — previously fired from Liquid on the /contact success
       render (with a sessionStorage guard against a refresh of the
       ?contact_posted=true URL). There is no reload now, so the guard is gone and
       the event fires here, on the confirmed API response. Payload is the same
       GA4 ecommerce shape: window.atomicProductDL supplies the PIM-aware item
       (datalayer-product-item.liquid), with the picked variant's sku/title
       overlaid so the event names the variant actually signed up for. */
    pushDataLayerEvent() {
      const pdl = window.atomicProductDL;
      let item;
      let currency;

      if (pdl && pdl.item) {
        item = JSON.parse(JSON.stringify(pdl.item));
        currency = pdl.currency;
      } else {
        item = {
          item_id: this.dataset.dlItemId || '',
          item_name: this.dataset.dlItemName || '',
          item_brand: this.dataset.dlItemBrand || '',
          item_category: this.dataset.dlItemCategory || '',
          item_variant: null,
          price: Number(this.dataset.dlItemPrice || 0),
          quantity: 1,
        };
        currency = this.dataset.dlCurrency || '';
      }

      const sku = this.form.querySelector('[data-bis-sku-input]')?.value || '';
      const variantTitle = this.form.querySelector('[data-bis-variant-input]')?.value || '';
      if (sku) item.item_id = sku;
      if (variantTitle && variantTitle !== 'Default Title') item.item_variant = variantTitle;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'product_stock_alert',
        ecommerce: { currency: currency, value: item.price, items: [item] },
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.isDone || this.isSubmitting) return;
      if (!this.validate()) return;

      if (!window.AtomicApi) {
        this.showError(this.strings.apiError, null);
        return;
      }

      const payload = this.getPayload();
      // All five keys are mandatory. A blank one (no SKU on the variant, an
      // unresolved market) would be rejected downstream anyway — fail here with
      // the generic message rather than sending a call that cannot succeed.
      if (!payload.email || !payload.country || !payload.language || !payload.product_sku || !payload.variant_id) {
        this.showError(this.strings.apiError, null);
        return;
      }

      this.setLoading(true);
      const run = ++this.submitId;
      const response = await window.AtomicApi.post('/back-in-stock/subscribe', payload, {
        includeLocale: false,
      });
      // The drawer closed and reset the form while this was in flight — the
      // signup still went through, but there is no state left to report it to.
      if (run !== this.submitId) return;
      const status = response && response.ok ? response.data && response.data.status : null;

      if (status === 'already_subscribed') {
        this.showSuccess(this.strings.alreadySubscribed);
        return;
      }
      if (status === 'subscribed') {
        this.showSuccess(this.strings.successMessage);
        this.pushDataLayerEvent();
        return;
      }

      this.showError(this.strings.apiError, null);
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
}
